import { useEffect, useState } from "react";
import {
    disableScheuler, enableScheuler, fetchCommentPoller,
    getSchedulerStatus, getWebhookEndpoint, updateCommentPoller,
    UpdateWebhookEndpoint
} from "@/api/setting-api";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { toast } from "@/hooks/use-toast";
import { Save, Activity, Globe, Timer, RefreshCw } from "lucide-react";

export default function SchedulerSetting() {
    const [schedulerStatus, setSchedulerStatus] = useState<boolean>(false);
    const [webhookUrl, setWebhookUrl] = useState<string>("");
    const [duration, setDuration] = useState<number>(60);
    const [loading, setLoading] = useState<Record<string, boolean>>({});

    const fetchAll = async () => {
        const [status, url, poll] = await Promise.all([
            getSchedulerStatus(),
            getWebhookEndpoint(),
            fetchCommentPoller()
        ]);
        if (status.success) setSchedulerStatus(status.data);
        if (url.success) setWebhookUrl(url.data.replace('/api/v2/fb/webhook', ''));
        if (poll.success) setDuration(poll.data);
    };

    useEffect(() => { fetchAll(); }, []);

    const notify = () => toast({ title: "Success", description: "Settings updated successfully." });

    const toggleScheduler = async (value: boolean) => {
        setSchedulerStatus(value); // Optimistic UI
        const resp = value ? await enableScheuler() : await disableScheuler();
        if (resp.success) notify();
    };

    const handleUpdate = async (key: string, fn: () => Promise<any>) => {
        setLoading(prev => ({ ...prev, [key]: true }));
        const resp = await fn();
        if (resp.success) notify();
        setLoading(prev => ({ ...prev, [key]: false }));
    };

    return (
        <Card className="shadow-sm border-slate-200">
            <CardHeader className="pb-4">
                <div className="flex items-center gap-2">
                    <Activity className={`w-5 h-5 ${schedulerStatus ? 'text-green-500' : 'text-slate-400'}`} />
                    <CardTitle>Automation Scheduler</CardTitle>
                </div>
                <CardDescription>
                    Control how the system synchronizes data with Facebook.
                </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
                {/* Master Switch Section */}
                <div className="flex items-center justify-between p-4 rounded-xl border bg-slate-50/50">
                    <div className="space-y-0.5">
                        <Label className="text-base font-semibold">Auto-Sync Status</Label>
                        <p className="text-xs text-slate-500">Automatically pull comments and posts from connected pages.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${schedulerStatus ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>
                            {schedulerStatus ? "ACTIVE" : "PAUSED"}
                        </span>
                        <Switch checked={schedulerStatus} onCheckedChange={toggleScheduler} />
                    </div>
                </div>

                <div className="space-y-4">
                    {/* FB Endpoint Input */}
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                            <Globe className="w-3 h-3" /> Facebook Webhook Endpoint
                        </Label>
                        <div className="flex items-center gap-2">
                            <div className="flex flex-1 items-center bg-white border rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-1 transition-all">
                                <Input
                                    className="border-none focus-visible:ring-0 shadow-none"
                                    placeholder="https://your-domain.com"
                                    value={webhookUrl}
                                    onChange={(e) => setWebhookUrl(e.target.value)}
                                />
                                <span className="bg-slate-100 px-3 py-2 text-xs font-mono text-slate-500 border-l shrink-0">
                                    /api/v2/fb/webhook
                                </span>
                            </div>
                            <Button
                                size="icon"
                                onClick={() => handleUpdate('endpoint', () => UpdateWebhookEndpoint(webhookUrl + "/api/v2/fb/webhook"))}
                                disabled={loading['endpoint']}
                            >
                                {loading['endpoint'] ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            </Button>
                        </div>
                    </div>

                    {/* Comment Poller Input */}
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                            <Timer className="w-3 h-3" /> Comment Poller Interval
                        </Label>
                        <div className="flex items-center gap-2">
                            <div className="relative flex-1">
                                <Input
                                    type="number"
                                    className="pr-16"
                                    value={duration}
                                    onChange={(e) => setDuration(parseInt(e.target.value) || 0)}
                                />
                                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                                    <span className="text-xs text-slate-400 font-medium">seconds</span>
                                </div>
                            </div>
                            <Button
                                size="icon"
                                variant="secondary"
                                onClick={() => handleUpdate('poller', () => updateCommentPoller(duration))}
                                disabled={loading['poller']}
                            >
                                {loading['poller'] ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            </Button>
                        </div>
                        <p className="text-[11px] text-slate-400 italic">Lower values increase server load but sync comments faster.</p>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}